import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import PropTypes from "prop-types";

import POSActivityHost
  from "../POSActivity/POSActivityHost";


export const RETAIL_POS_APPLICATION_HOST_STATUS =
  Object.freeze({
    IDLE:
      "idle",

    LOADING_PUBLICATION:
      "loading_publication",

    READY:
      "ready",

    PUBLICATION_ERROR:
      "publication_error",
  });


const EMPTY_ARRAY =
  Object.freeze([]);


/**
 * Retail POS Application Host.
 *
 * Thin React boundary responsible only for resolving the
 * current publication before delegating domain/runtime
 * rendering to the generic POSActivityHost.
 *
 * The component is deliberately route-agnostic:
 *
 * companyUuid and activityUuid are explicit inputs.
 *
 * React Router wiring belongs to LOT 12.8.7.8.4.
 */
export default function RetailPOSApplicationHost({
  composition,

  companyUuid,
  activityUuid,
  activityType = null,

  permissions = EMPTY_ARRAY,
  features = EMPTY_ARRAY,
  subscription = null,

  renderState = null,
  onResolved = null,
  onError = null,
}) {
  const requestIdRef =
    useRef(0);

  const [
    publicationState,
    setPublicationState,
  ] = useState(() => ({
    status:
      RETAIL_POS_APPLICATION_HOST_STATUS
        .IDLE,

    activePublication:
      null,

    error:
      null,
  }));


  useEffect(
    () => {
      const requestId =
        requestIdRef.current + 1;

      requestIdRef.current =
        requestId;

      let active = true;

      const resolver =
        composition
          ?.activePublicationResolver;

      if (
        !isNonEmptyString(companyUuid)
        || !isNonEmptyString(activityUuid)
      ) {
        setPublicationState({
          status:
            RETAIL_POS_APPLICATION_HOST_STATUS
              .IDLE,

          activePublication:
            null,

          error:
            null,
        });

        return () => {
          active = false;
        };
      }

      if (
        !resolver
        || typeof resolver
          .resolveActivePublication
          !== "function"
      ) {
        const error =
          createApplicationHostError(
            "Retail POS application composition must expose activePublicationResolver.resolveActivePublication().",
            {
              code:
                "INVALID_ACTIVE_PUBLICATION_RESOLVER",
            }
          );

        setPublicationState({
          status:
            RETAIL_POS_APPLICATION_HOST_STATUS
              .PUBLICATION_ERROR,

          activePublication:
            null,

          error,
        });

        if (
          typeof onError === "function"
        ) {
          onError(error);
        }

        return () => {
          active = false;
        };
      }

      setPublicationState({
        status:
          RETAIL_POS_APPLICATION_HOST_STATUS
            .LOADING_PUBLICATION,

        activePublication:
          null,

        error:
          null,
      });

      Promise
        .resolve(
          resolver
            .resolveActivePublication({
              companyUuid:
                companyUuid.trim(),

              activityUuid:
                activityUuid.trim(),
            })
        )
        .then(
          (activePublication) => {
            if (
              !active
              || requestId
                !== requestIdRef.current
            ) {
              return;
            }

            setPublicationState({
              status:
                RETAIL_POS_APPLICATION_HOST_STATUS
                  .READY,

              activePublication:
                activePublication
                ?? null,

              error:
                null,
            });
          }
        )
        .catch(
          (sourceError) => {
            if (
              !active
              || requestId
                !== requestIdRef.current
            ) {
              return;
            }

            const error =
              createApplicationHostError(
                "Unable to resolve the active Retail POS publication.",
                {
                  code:
                    "ACTIVE_PUBLICATION_RESOLUTION_FAILED",

                  cause:
                    sourceError,
                }
              );

            setPublicationState({
              status:
                RETAIL_POS_APPLICATION_HOST_STATUS
                  .PUBLICATION_ERROR,

              activePublication:
                null,

              error,
            });

            if (
              typeof onError
                === "function"
            ) {
              onError(error);
            }
          }
        );

      return () => {
        active = false;
      };
    },
    [
      composition,
      companyUuid,
      activityUuid,
      onError,
    ]
  );


  const hasExplicitScope =
    isNonEmptyString(companyUuid)
    && isNonEmptyString(activityUuid);


  if (
    hasExplicitScope
    && (
      publicationState.status
        === RETAIL_POS_APPLICATION_HOST_STATUS
          .IDLE
      || publicationState.status
        === RETAIL_POS_APPLICATION_HOST_STATUS
          .LOADING_PUBLICATION
    )
  ) {
    return renderApplicationState({
      renderState,

      status:
        RETAIL_POS_APPLICATION_HOST_STATUS
          .LOADING_PUBLICATION,

      error:
        null,

      defaultNode: (
        <div
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          Chargement de la publication POS…
        </div>
      ),
    });
  }


  if (
    publicationState.status
      === RETAIL_POS_APPLICATION_HOST_STATUS
        .PUBLICATION_ERROR
  ) {
    return renderApplicationState({
      renderState,

      status:
        publicationState.status,

      error:
        publicationState.error,

      defaultNode: (
        <div
          role="alert"
          aria-live="assertive"
        >
          Une erreur empêche le chargement
          de la publication POS.
        </div>
      ),
    });
  }


  const posActivityDomainResolver =
    composition
      ?.posActivityDomainResolver;


  if (
    !posActivityDomainResolver
    || typeof posActivityDomainResolver
      .resolvePOSActivityDomain
      !== "function"
  ) {
    const error =
      createApplicationHostError(
        "Retail POS application composition must expose posActivityDomainResolver.resolvePOSActivityDomain().",
        {
          code:
            "INVALID_POS_ACTIVITY_DOMAIN_RESOLVER",
        }
      );

    return renderApplicationState({
      renderState,

      status:
        RETAIL_POS_APPLICATION_HOST_STATUS
          .PUBLICATION_ERROR,

      error,

      defaultNode: (
        <div
          role="alert"
          aria-live="assertive"
        >
          Une erreur empêche le chargement
          du domaine POS.
        </div>
      ),
    });
  }


  return (
    <POSActivityHost
      resolver={
        posActivityDomainResolver
      }

      companyUuid={
        companyUuid
      }

      activityUuid={
        activityUuid
      }

      activityType={
        activityType
      }

      permissions={
        permissions
      }

      features={
        features
      }

      subscription={
        subscription
      }

      activePublication={
        publicationState
          .activePublication
      }

      renderState={
        renderState
      }

      onResolved={
        onResolved
      }

      onError={
        onError
      }
    />
  );
}


function renderApplicationState({
  renderState,
  status,
  error,
  defaultNode,
}) {
  if (
    typeof renderState === "function"
  ) {
    return renderState({
      status,
      error,
    });
  }

  return defaultNode;
}


function isNonEmptyString(
  value
) {
  return (
    typeof value === "string"
    && Boolean(value.trim())
  );
}


function createApplicationHostError(
  message,
  {
    code,
    cause = null,
  }
) {
  const error =
    new Error(message);

  error.name =
    "RetailPOSApplicationHostError";

  error.code =
    code;

  if (cause !== null) {
    error.cause =
      cause;
  }

  return error;
}


RetailPOSApplicationHost.propTypes = {
  composition:
    PropTypes.shape({
      activePublicationResolver:
        PropTypes.shape({
          resolveActivePublication:
            PropTypes.func.isRequired,
        }).isRequired,

      posActivityDomainResolver:
        PropTypes.shape({
          resolvePOSActivityDomain:
            PropTypes.func.isRequired,
        }).isRequired,
    }).isRequired,

  companyUuid:
    PropTypes.string,

  activityUuid:
    PropTypes.string,

  activityType:
    PropTypes.string,

  permissions:
    PropTypes.array,

  features:
    PropTypes.array,

  subscription:
    PropTypes.any,

  renderState:
    PropTypes.func,

  onResolved:
    PropTypes.func,

  onError:
    PropTypes.func,
};
